import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Gauge, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

/* Test-Bibliothek (13.10.2026, gesamtabgleich A3): Kraft, Ausdauer, Beweglichkeit, Balance, Umfänge — erster gegen letzter
   Wert, „besser“ nach der Richtung des Tests. Belegt die Fortschritts-Garantie. Dieselbe Karte im CRM und auf /coach. */

const zahl = (v) => (v === null || v === undefined ? '–' : Number(v).toLocaleString('de-DE', { maximumFractionDigits: 2 }));
const tag = (iso) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(2, 4)}`;
const URTEIL = { besser: 'bg-primary text-primary-foreground', schlechter: 'bg-destructive/15 text-destructive', gleich: 'bg-muted text-muted-foreground' };
const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

export default function Leistungstests({ laden, eintragen, loeschen, testId = 'leistungstests' }) {
  const [d, setD] = useState(null);
  const [neu, setNeu] = useState({ test: '', wert: '', datum: '' });
  const lade = useCallback(() => { laden().then((r) => setD(r.data)).catch(() => setD(null)); }, [laden]);
  useEffect(() => { lade(); }, [lade]);
  const gruppen = useMemo(() => {
    if (!d) return [];
    return Object.entries(d.bereiche).map(([k, v]) => [v, Object.entries(d.katalog).filter(([, t]) => t.bereich === k)]);
  }, [d]);
  if (!d) return <Skeleton className="h-24" />;
  const t = d.katalog[neu.test];
  const speichern = async () => {
    try { await eintragen({ test: neu.test, wert: neu.wert, datum: neu.datum || undefined }); toast.success('Gespeichert'); setNeu({ test: neu.test, wert: '', datum: '' }); lade(); }
    catch (e) { toast.error(fehlerText(e, 'Nicht gespeichert')); }
  };
  const weg = async (m) => {
    if (!window.confirm('Messung löschen?')) return;
    try { await loeschen(m.id); lade(); } catch (e) { toast.error(fehlerText(e, 'Nicht gelöscht')); }
  };
  return (
    <Card className="border-border/60" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base"><Gauge className="h-4 w-4 text-primary" /> Tests
          {d.besser > 0 && <Badge className="border-transparent bg-primary text-[10px] text-primary-foreground hover:bg-primary">{d.besser} besser als am Start</Badge>}
        </CardTitle>
        <p className="text-xs text-muted-foreground">Am Start und am Ende des Coachings messen – der Vergleich steht im Abschlussbericht.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <select aria-label="Test" className="h-10 min-w-[12rem] flex-1 rounded-md border border-input bg-background px-2 text-sm" value={neu.test} onChange={(e) => setNeu({ ...neu, test: e.target.value })}>
            <option value="">Test wählen …</option>
            {gruppen.map(([name, liste]) => (
              <optgroup key={name} label={name}>{liste.map(([k, x]) => <option key={k} value={k}>{x.titel} ({x.einheit})</option>)}</optgroup>
            ))}
          </select>
          <Input aria-label="Wert" inputMode="decimal" className="w-28" placeholder={t ? t.einheit : 'Wert'} value={neu.wert} onChange={(e) => setNeu({ ...neu, wert: e.target.value })} />
          <Input aria-label="Datum" type="date" className="w-40" value={neu.datum} onChange={(e) => setNeu({ ...neu, datum: e.target.value })} />
          <Button size="sm" className="h-10" disabled={!neu.test || neu.wert === ''} onClick={speichern}>Eintragen</Button>
        </div>
        {t?.anleitung && <p className="text-xs text-muted-foreground">{t.anleitung} {t.richtung === 'niedriger' ? '(weniger ist besser)' : ''}</p>}
        {!d.tests.length && <p className="text-sm text-muted-foreground">Noch keine Messung.</p>}
        <ul className="divide-y divide-border/60">
          {d.tests.map((z) => (
            <li key={z.test} className="space-y-1 py-2">
              <p className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold">{z.titel}</span>
                <span className="tabular-nums text-muted-foreground">{zahl(z.erster.wert)} → <b className="text-foreground">{zahl(z.letzter.wert)}</b> {z.einheit}</span>
                {z.urteil && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${URTEIL[z.urteil]}`}>{z.urteil}{z.prozent !== null ? ` ${z.prozent > 0 ? '+' : ''}${String(z.prozent).replace('.', ',')} %` : ''}</span>}
              </p>
              <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                {z.messungen.map((m) => (
                  <span key={m.id} className="inline-flex items-center gap-1">{tag(m.datum)}: {zahl(m.wert)}{m.von?.art === 'mitglied' ? ' (selbst)' : ''}
                    <button type="button" aria-label="Messung löschen" className="opacity-60 hover:opacity-100" onClick={() => weg(m)}><Trash2 className="h-3 w-3" /></button>
                  </span>
                ))}
              </p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function LeistungstestsCrm({ leadId }) {
  const f = useMemo(() => ({
    laden: () => api.get(`/tests/mitglied/${leadId}`),
    eintragen: (b) => api.post(`/tests/mitglied/${leadId}`, b),
    loeschen: (mid) => api.delete(`/tests/mitglied/${leadId}/${mid}`),
  }), [leadId]);
  return <Leistungstests key={leadId} {...f} />;
}
